import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getData } from "../../utils/bloombergMarketData";
import { fetchTodaysSymbols } from "../../component/Methods";
import { removeUser } from "../Actions/UserActions";

import {
  buildMarketDataTickerFields,
  mergeMarketDataEntries,
} from "../../utils/livePriceTickerMatch";

// Async Thunk Actions
export const requestMarketData = createAsyncThunk(
  "marketData/requestMarketData",
  async (payload, { dispatch, getState }) => {
    const getUniqueTickers = (array) => {
      const uniqueSymbols = new Set();
      return array
        .filter((item) => {
          if (!item?.ticker?.symbol) return false;
          const symbol = item.ticker.symbol.split(" ")[0];
          if (!uniqueSymbols.has(symbol)) {
            uniqueSymbols.add(symbol);
            return true;
          }
          return false;
        })
        .map((item) => item.ticker);
    };

    const state = getState();
    const livePrices = state.marketData.livePrices;
    const apiInfo = state?.users?.apiInfo;
    const userInfo = state?.users?.userInfo;
    const market = String(payload.market || "bloomberg").toLowerCase();
    const isFmp = market === "fmp";

    const lotsForRequest = isFmp
      ? (payload.lots || []).filter((lot) => lot?.ticker?.investment !== 9)
      : payload.lots || [];

    const getPrices = async (symbols, livePrices) => {
      const data = [];
      const chunkSize = payload?.noRequest ? 4000 : 4000;
      const sendReq = payload?.noRequest ? false : true;

      for (let i = 0; i < symbols.length; i += chunkSize) {
        const symbolsChunk = symbols.slice(i, i + chunkSize);
        const response = await getData(
          symbolsChunk,
          payload.threshold,
          sendReq,
          true,
          false,
          payload.market ? payload.market : "bloomberg",
          livePrices,
          payload.noApiHit ? false : true,
          apiInfo,
          userInfo
        );
        data.push(...response[0]);
      }
      return data;
    };

    const uniqueTickers = getUniqueTickers(lotsForRequest);

    let uniqueArray = uniqueTickers;

    if (payload.trades) {
      const todaysSymbols = fetchTodaysSymbols(payload.trades);
      const tradeSymbols = isFmp
        ? (todaysSymbols || []).filter((t) => t?.investment !== 9)
        : todaysSymbols;
      const mergedArray = [...uniqueTickers, ...tradeSymbols];

      uniqueArray = mergedArray.filter(
        (item, index, self) =>
          self.findIndex((t) => t.symbol === item.symbol) === index
      );
    }

    const tickersToFetch =
      uniqueArray.length > 0 ? uniqueArray : uniqueTickers;

    const fetchedData = await getPrices(tickersToFetch, livePrices);

    return fetchedData;
  }
);

const initialState = {
  marketData: [],
  markets: [],
  livePrices: [],
  companyNames: [],
  signalsPerformance: [],
  RawMarketData: {},
  marketDataThreshold: 20,
  calculatedMonitorScreen: [],
  showMarketLoader: false,
  loading: false,
  error: null,
};

const marketDataSlice = createSlice({
  name: "marketData",
  initialState,
  reducers: {
    clearMarketDataError: (state) => {
      state.error = null;
    },
    setMarketDataLoading: (state, action) => {
      state.loading = action.payload;
    },
    setShowMarketLoader: (state, action) => {
      state.showMarketLoader = action.payload;
    },
    setMarkets: (state, action) => {
      state.markets = action.payload;
    },
    setCalculatedMonitorScreen: (state, action) => {
      state.calculatedMonitorScreen = action.payload;
    },
    setMarketDataThreshold: (state, action) => {
      state.marketDataThreshold = action.payload;
    },
    setMarketData: (state, action) => {
      function processData(jsonData) {
        const resultArray = [];
        const companyArray = [];
        const signalsArray = [];
        let currentPrice = 1;
        let yesterdayPrice = 1;
        let expire = Date.now();
        let companyName = "";
        let PercentChangeDay = 0;
        let PercentChangeMonth = 0;
        let PercentChangeQuarter = 0;
        let PercentChangeWeek = 0;
        let marketCap = 0;
        let sector = "";
        let expire2 = Date.now();
        let expire3 = Date.now();

        if (jsonData.data) {
          if (jsonData.data.keys) {
            for (const key in jsonData.data.keys) {
              let marketKey = "";
              yesterdayPrice = 1;
              currentPrice = 1;      
              expire = Date.now();  
              if (jsonData.data.values.hasOwnProperty(key)) {
                const tickerFields = buildMarketDataTickerFields(
                  jsonData.data.keys[key]
                );

                if (jsonData.data.values[key]["9"]) {
                  const currentPriceKey = Object.keys(
                    jsonData.data.values[key]["9"]
                  )[0];
                  marketKey = currentPriceKey;

                  currentPrice =
                    jsonData.data.values[key]["9"][currentPriceKey].value;

                  expire = jsonData.data.values[key]["9"][currentPriceKey].date;
                }

                if (jsonData.data.values[key]["10"]) {
                  const yesterdayPriceKey = Object.keys(
                    jsonData.data.values[key]["10"]
                  )[0];
                  yesterdayPrice =
                    jsonData.data.values[key]["10"][yesterdayPriceKey].value;

                  marketKey = yesterdayPriceKey;
                }

                if (currentPrice != 1) {
                  resultArray.push({
                    ...tickerFields,
                    price: currentPrice,
                    yestPrice: yesterdayPrice,
                    expiration: expire,
                    market: marketKey,
                  });
                }
              }
            }
          }
        }

        if (jsonData.keys) {
          for (const key in jsonData.keys) {
            companyName = "";
            yesterdayPrice = 1;
            currentPrice = 1;        
            expire = Date.now();     
            PercentChangeDay = 0;
            PercentChangeMonth = 0;
            PercentChangeQuarter = 0;
            PercentChangeWeek = 0;
            marketCap = 0;
            sector = "";
            let marketKey = "";
            if (jsonData.values.hasOwnProperty(key)) {
              const tickerFields = buildMarketDataTickerFields(jsonData.keys[key]);

              // PRICE and YESTERDAY'S PRICE
              if (jsonData.values[key]["9"]) {
                const currentPriceKey = Object.keys(
                  jsonData.values[key]["9"]
                )[0];
                marketKey = currentPriceKey;

                currentPrice = jsonData.values[key]["9"][currentPriceKey].value;
                expire = jsonData.values[key]["9"][currentPriceKey].date;
              }

              if (jsonData.values[key]["10"]) {
                const yesterdayPriceKey = Object.keys(
                  jsonData.values[key]["10"]
                )[0];
                yesterdayPrice =
                  jsonData.values[key]["10"][yesterdayPriceKey].value;
                marketKey = yesterdayPriceKey;
              }

              // COMPANY NAMES
              if (jsonData.values[key]["1"]) {
                const companyNameKey = Object.keys(
                  jsonData.values[key]["1"]
                )[0];
                companyName = jsonData.values[key]["1"][companyNameKey].value;

                expire2 = jsonData.values[key]["1"][companyNameKey].date;
                marketKey = companyNameKey;
              }

              // SIGNALS PERFORMANCE
              if (jsonData.values[key]["4"]) {
                const marketCapKey = Object.keys(jsonData.values[key]["4"])[0];
                marketCap = jsonData.values[key]["4"][marketCapKey].value;
                marketKey = marketCapKey;
              }

              if (jsonData.values[key]["5"]) {
                const PercentChangeDayKey = Object.keys(
                  jsonData.values[key]["5"]
                )[0];
                PercentChangeDay =
                  jsonData.values[key]["5"][PercentChangeDayKey].value;
                expire3 = jsonData.values[key]["5"][PercentChangeDayKey].date;
                marketKey = PercentChangeDayKey;
              }
              if (jsonData.values[key]["6"]) {
                const PercentChangeMonthKey = Object.keys(
                  jsonData.values[key]["6"]
                )[0];
                PercentChangeMonth =
                  jsonData.values[key]["6"][PercentChangeMonthKey].value;
                marketKey = PercentChangeMonthKey;
              }
              if (jsonData.values[key]["7"]) {
                const PercentChangeQuarterKey = Object.keys(
                  jsonData.values[key]["7"]
                )[0];
                PercentChangeQuarter =
                  jsonData.values[key]["7"][PercentChangeQuarterKey].value;
                marketKey = PercentChangeQuarterKey;
              }
              if (jsonData.values[key]["8"]) {
                const PercentChangeWeekKey = Object.keys(
                  jsonData.values[key]["8"]
                )[0];
                PercentChangeWeek =
                  jsonData.values[key]["8"][PercentChangeWeekKey].value;
                marketKey = PercentChangeWeekKey;
              }

              if (jsonData.values[key]["11"]) {
                const sectorKey = Object.keys(jsonData.values[key]["11"])[0];
                sector = jsonData.values[key]["11"][sectorKey].value;
                marketKey = sectorKey;
              }

              if (companyName != "") {
                companyArray.push({
                  ...tickerFields,
                  company: companyName,
                  expiration: expire2,
                  market: marketKey,
                });
              }

              if (currentPrice != 1) {
                resultArray.push({
                  ...tickerFields,
                  price: currentPrice,
                  yestPrice: yesterdayPrice,
                  expiration: expire,
                  market: marketKey,
                });
              }

              if (PercentChangeDay != 0) {
                signalsArray.push({
                  ...tickerFields,
                  expiration: expire3,
                  PercentChangeDay: PercentChangeDay,
                  PercentChangeMonth: PercentChangeMonth,
                  PercentChangeQuarter: PercentChangeQuarter,
                  PercentChangeWeek: PercentChangeWeek,
                  marketCap: marketCap,
                  sector: sector,
                  market: marketKey,
                });
              }
            }
          }
        }

        return [resultArray, companyArray, signalsArray];
      }

      const socketData = processData(action.payload)[0];
      const companyData = processData(action.payload)[1];
      const signalsData = processData(action.payload)[2];

      if (socketData.length > 0) {
        state.livePrices = mergeMarketDataEntries(state.livePrices, socketData);
      }

      if (companyData.length > 0) {
        state.companyNames = mergeMarketDataEntries(
          state.companyNames,
          companyData
        );
      }

      if (signalsData.length > 0) {
        state.signalsPerformance = mergeMarketDataEntries(
          state.signalsPerformance,
          signalsData
        );
      }

      state.RawMarketData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestMarketData.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestMarketData.fulfilled, (state, action) => {
        state.marketData = action.payload;
        state.loading = false;
      })
      .addCase(requestMarketData.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(removeUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeUser.fulfilled, (state) => {
        state.marketData = [];
        state.livePrices = [];
        state.companyNames = [];
        state.signalsPerformance = [];
        state.RawMarketData = {};
        state.markets = [];
        state.calculatedMonitorScreen = [];
        state.loading = false;
      })
      .addCase(removeUser.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { 
  clearMarketDataError, 
  setMarketDataLoading, 
  setShowMarketLoader,
  setMarkets,
  setCalculatedMonitorScreen,
  setMarketDataThreshold,
  setMarketData
} = marketDataSlice.actions;
export default marketDataSlice.reducer;

// Selectors
export const selectMarketData = (state) => state.marketData.marketData;
export const selectMarkets = (state) => state.marketData.markets;
export const selectLivePrices = (state) => state.marketData.livePrices;
export const selectCompanyNames = (state) => state.marketData.companyNames;
export const selectSignalsPerformance = (state) => state.marketData.signalsPerformance;
export const selectRawMarketData = (state) => state.marketData.RawMarketData;
export const selectMarketDataThreshold = (state) => state.marketData.marketDataThreshold;
export const selectCalculatedMonitorScreen = (state) => state.marketData.calculatedMonitorScreen;
export const selectShowMarketLoader = (state) => state.marketData.showMarketLoader;
export const selectMarketDataLoading = (state) => state.marketData.loading;
export const selectMarketDataError = (state) => state.marketData.error;

// Legacy selectors for backward compatibility
export const getMarketDataThreshold = (state) => state.marketData.marketDataThreshold;
export const getLivePrices = (state) => state.marketData.livePrices;
